import { gql, TypedDocumentNode } from '@apollo/client';

import {
  AttributeCreateBulkInput,
  AttributeCreateInput,
  AttributeDeleteInput,
  AttributeGroupCreateInput,
  AttributeGroupDeleteInput,
  AttributeGroupUpdateBulkInput,
  AttributeGroupUpdateInput,
  AttributeUpdateBulkInput,
  AttributeUpdateInput,
  BasicSchemasConnection,
  BasicSchemasInput,
  ElementCreateInput,
  ElementGroupCreateInput,
  ElementGroupDeleteInput,
  ElementGroupUpdateInput,
  ElementUpdateInput,
  Schema,
  SchemaConversionUnitCreateBulkInput,
  SchemaConversionUnitCreateInput,
  SchemaConversionUnitDeleteBulkInput,
  SchemaConversionUnitUpdateBulkInput,
  SchemaConversionUnitUpdateInput,
  SchemaCreateInput,
  SchemasConnection,
  SchemasInput,
  SchemaTree,
  SchemaUpdateInput,
} from '@atom/types/schema';

export const BASE_PORTAL_SCHEMA_FRAGMENT = gql`
  fragment BaseBasicSchema on BasicSchema {
    id
    name
    assetType
    colorId
    markerId
    locationType
    isMaterial
    lrsRequired
    updatedBy {
      firstName
      lastName
    }
    updatedDate
    isPublished
    createdBy {
      firstName
      lastName
    }
    createdDate
  }
`;

export const BASE_SCHEMA_FRAGMENT = gql`
  fragment BaseSchema on Schema {
    id
    name
    assetType
    attributes
    attributeGroups {
      id
      name
      expandByDefault
      attributes
    }
    elements {
      id
      name
      group
      markerId
    }
    budget {
      type
      stockBasedOption
      startEndReadingOption
      rate
      unit
      conversionUnits {
        id
        unit
        conversionNumber
        active
        isDefault
        createdBy {
          firstName
          lastName
        }
        createdDate
        updatedBy {
          firstName
          lastName
        }
        updatedDate
      }
    }
    assetCount
    elementCount
    colorId
    markerId
    isMaterial
    locationType
    lrsRequired
    ciEnabled
    pciEnabled
  }
`;

export const BASE_SCHEMA_TREE_FRAGMENT = gql`
  fragment BaseSchemaTree on SchemaTree {
    id
    name
    isPublished
    isMaterial
    locationType
    markerId
    colorId
    group
    assetType
    attributeGroups {
      id
      name
      expandByDefault
      attributes {
        id
        name
        dataType
        defaultValue
        enumeration
        unit
        isVisibleAsSubtext
        isFilterable
        isEditable
        isRequired
        thousandsSeparator
      }
    }
    elements
    elementGroups {
      id
      name
      elements
    }
    budget {
      type
      stockBasedOption
      startEndReadingOption
      rate
      unit
      conversionUnits {
        id
        unit
        conversionNumber
        active
        isDefault
        createdBy {
          firstName
          lastName
        }
        createdDate
        updatedBy {
          firstName
          lastName
        }
        updatedDate
      }
    }
    createdBy
    createdDate
    updatedBy
    updatedDate
  }
`;

export const GET_SCHEMA: TypedDocumentNode<{ schema: Schema }, { id: string }> =
  gql`
    query schema($id: ID!) {
      schema(id: $id) {
        ...BaseSchema
      }
    }
    ${BASE_SCHEMA_FRAGMENT}
  `;

export const GET_SCHEMAS: TypedDocumentNode<
  { schemas: SchemasConnection },
  { input: SchemasInput }
> = gql`
  query schemas($input: SchemasInput!) {
    schemas(input: $input) {
      totalCount
      schemas {
        ...BaseSchema
      }
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const GET_BASIC_SCHEMAS: TypedDocumentNode<
  { basicSchemas: BasicSchemasConnection },
  { input: BasicSchemasInput }
> = gql`
  query basicSchemas($input: BasicSchemasInputConnectionInput!) {
    basicSchemas(input: $input) {
      totalCount
      basicSchemas {
        ...BaseBasicSchema
      }
    }
  }
  ${BASE_PORTAL_SCHEMA_FRAGMENT}
`;

export const GET_SCHEMA_TREE: TypedDocumentNode<
  { schemaTree: SchemaTree },
  { id: string }
> = gql`
  query schemaTree($id: ID!) {
    schemaTree(id: $id) {
      ...BaseSchemaTree
    }
  }
  ${BASE_SCHEMA_TREE_FRAGMENT}
`;

export const SCHEMA_CREATE: TypedDocumentNode<
  { schemaCreate: Schema },
  { input: SchemaCreateInput }
> = gql`
  mutation schemaCreate($input: SchemaCreateInput!) {
    schemaCreate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const SCHEMA_DUPLICATE: TypedDocumentNode<
  { schemaDuplicate: Schema },
  { id: string }
> = gql`
  mutation schemaDuplicate($id: ID!) {
    schemaDuplicate(id: $id) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const SCHEMA_UPDATE: TypedDocumentNode<
  { schemaUpdate: Schema },
  { input: SchemaUpdateInput }
> = gql`
  mutation schemaUpdate($input: SchemaUpdateInput!) {
    schemaUpdate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const SCHEMA_DELETE: TypedDocumentNode<
  { schemaDelete: boolean },
  { id: string }
> = gql`
  mutation schemaDelete($id: ID!) {
    schemaDelete(id: $id)
  }
`;

export const SCHEMA_CONVERSION_UNIT_CREATE: TypedDocumentNode<
  { schemaConversionUnitCreate: Schema },
  { input: SchemaConversionUnitCreateInput }
> = gql`
  mutation schemaConversionUnitCreate(
    $input: SchemaConversionUnitCreateInput!
  ) {
    schemaConversionUnitCreate(input: $input) {
      id
    }
  }
`;

export const SCHEMA_CONVERSION_UNIT_UPDATE: TypedDocumentNode<
  { schemaConversionUnitUpdate: Schema },
  { input: SchemaConversionUnitUpdateInput }
> = gql`
  mutation schemaConversionUnitUpdate(
    $input: SchemaConversionUnitUpdateInput!
  ) {
    schemaConversionUnitUpdate(input: $input) {
      id
    }
  }
`;

export const SCHEMA_CONVERSION_UNIT_CREATE_BULK: TypedDocumentNode<
  { schemaConversionUnitCreateBulk: Schema },
  { input: SchemaConversionUnitCreateBulkInput }
> = gql`
  mutation schemaConversionUnitCreateBulk(
    $input: SchemaConversionUnitCreateBulkInput!
  ) {
    schemaConversionUnitCreateBulk(input: $input) {
      id
    }
  }
`;

export const SCHEMA_CONVERSION_UNIT_UPDATE_BULK: TypedDocumentNode<
  { schemaConversionUnitUpdateBulk: Schema },
  { input: SchemaConversionUnitUpdateBulkInput }
> = gql`
  mutation schemaConversionUnitUpdateBulk(
    $input: SchemaConversionUnitUpdateBulkInput!
  ) {
    schemaConversionUnitUpdateBulk(input: $input) {
      id
    }
  }
`;

export const SCHEMA_CONVERSION_UNIT_DELETE_BULK: TypedDocumentNode<
  { schemaConversionUnitDeleteBulk: boolean },
  { input: SchemaConversionUnitDeleteBulkInput }
> = gql`
  mutation schemaConversionUnitDeleteBulk(
    $input: SchemaConversionUnitDeleteBulkInput!
  ) {
    schemaConversionUnitDeleteBulk(input: $input)
  }
`;

export const ELEMENT_CREATE: TypedDocumentNode<
  { elementCreate: Schema },
  { input: ElementCreateInput }
> = gql`
  mutation elementCreate($input: ElementCreateInput!) {
    elementCreate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ELEMENT_UPDATE: TypedDocumentNode<
  { elementUpdate: Schema },
  { input: ElementUpdateInput }
> = gql`
  mutation elementUpdate($input: ElementUpdateInput!) {
    elementUpdate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ELEMENT_DELETE: TypedDocumentNode<
  { elementDelete: boolean },
  { id: string }
> = gql`
  mutation elementDelete($id: ID!) {
    elementDelete(id: $id)
  }
`;

export const ELEMENT_GROUP_CREATE: TypedDocumentNode<
  { elementGroupCreate: Schema },
  { input: ElementGroupCreateInput }
> = gql`
  mutation elementGroupCreate($input: ElementGroupCreateInput!) {
    elementGroupCreate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ELEMENT_GROUP_UPDATE: TypedDocumentNode<
  { elementGroupUpdate: boolean },
  { input: ElementGroupUpdateInput }
> = gql`
  mutation elementGroupUpdate($input: ElementGroupUpdateInput!) {
    elementGroupUpdate(input: $input)
  }
`;

export const ELEMENT_GROUP_DELETE: TypedDocumentNode<
  { elementGroupDelete: boolean },
  { input: ElementGroupDeleteInput }
> = gql`
  mutation elementGroupDelete($input: ElementGroupDeleteInput!) {
    elementGroupDelete(input: $input)
  }
`;

export const ATTRIBUTE_GROUP_CREATE: TypedDocumentNode<
  { attributeGroupCreate: Schema },
  { input: AttributeGroupCreateInput }
> = gql`
  mutation attributeGroupCreate($input: AttributeGroupCreateInput!) {
    attributeGroupCreate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ATTRIBUTE_GROUP_UPDATE: TypedDocumentNode<
  { attributeGroupUpdate: Schema },
  { input: AttributeGroupUpdateInput }
> = gql`
  mutation attributeGroupUpdate($input: AttributeGroupUpdateInput!) {
    attributeGroupUpdate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ATTRIBUTE_GROUP_UPDATE_BULK: TypedDocumentNode<
  { attributeGroupUpdateBulk: Schema },
  { input: AttributeGroupUpdateBulkInput }
> = gql`
  mutation attributeGroupUpdateBulk($input: AttributeGroupUpdateBulkInput!) {
    attributeGroupUpdateBulk(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ATTRIBUTE_GROUP_DELETE: TypedDocumentNode<
  { attributeGroupDelete: boolean },
  { input: AttributeGroupDeleteInput }
> = gql`
  mutation attributeGroupDelete($input: AttributeGroupDeleteInput!) {
    attributeGroupDelete(input: $input)
  }
`;

export const ATTRIBUTE_CREATE: TypedDocumentNode<
  { attributeCreate: Schema },
  { input: AttributeCreateInput }
> = gql`
  mutation attributeCreate($input: AttributeCreateInput!) {
    attributeCreate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ATTRIBUTE_CREATE_BULK: TypedDocumentNode<
  { attributeCreateBulk: Schema },
  { input: AttributeCreateBulkInput }
> = gql`
  mutation attributeCreateBulk($input: AttributeCreateBulkInput!) {
    attributeCreateBulk(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ATTRIBUTE_UPDATE: TypedDocumentNode<
  { attributeUpdate: Schema },
  { input: AttributeUpdateInput }
> = gql`
  mutation attributeUpdate($input: AttributeUpdateInput!) {
    attributeUpdate(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ATTRIBUTE_UPDATE_BULK: TypedDocumentNode<
  { attributeUpdateBulk: Schema },
  { input: AttributeUpdateBulkInput }
> = gql`
  mutation attributeUpdateBulk($input: AttributeUpdateBulkInput!) {
    attributeUpdateBulk(input: $input) {
      ...BaseSchema
    }
  }
  ${BASE_SCHEMA_FRAGMENT}
`;

export const ATTRIBUTE_DELETE: TypedDocumentNode<
  { attributeDelete: boolean },
  { input: AttributeDeleteInput }
> = gql`
  mutation attributeDelete($input: AttributeDeleteInput!) {
    attributeDelete(input: $input)
  }
`;
